import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface CN56ReportParams {
  serviceTypes: string[];
  groupBy: string[];
  formatType: 'html' | 'pdf' | 'xls';
  fromQuarter?: number;
  toQuarter?: number;
  fromYear?: number;
  toYear?: number;
  table?: string;
  designatedOperator?: string;
}

// Interface for individual data items
export interface CN56DataItem {
  outboundOe: string | null;
  inboundOe: string | null;
  mailCategory: string | null;
  mailSubclass: string | null;
  month: number;
  totalPGross: number;
  totalPNet: number;
  totalGGross: number;
  totalGNet: number;
  totalEGross: number;
  totalENet: number;
  totalCGross: number;
  totalCNet: number;
  totalPgeGross: number;
  totalPgeNet: number;
  totalWeight5kg: number;
  totalWeight5kgActual: number;
  totalRitems: number;
  totalVitems: number;
  totalExpressItems: number;
  totalPGrossStr: string;
  totalPNetStr: string;
  totalGGrossStr: string;
  totalGNetStr: string;
  totalEGrossStr: string;
  totalENetStr: string;
  totalCGrossStr: string;
  totalCNetStr: string;
  totalPgeGrossStr: string;
  totalPgeNetStr: string;
  totalWeight5kgStr: string;
  totalWeight5kgActualStr: string;
  totalRitemsStr: string;
  totalVitemsStr: string;
  totalExpressItemsStr: string;
}

// Interface for header grouping information
export interface CN56HeaderGrouping {
  mailCategory: string | null;
  mailSubclass: string | null;
  outboundOe: string | null;
  inboundOe: string | null;
  month: number | null;
}

// Interface for totals (sampledTotal, nonSampledTotal, allFlowTotal)
export interface CN56TotalData {
  outboundOe: string | null;
  inboundOe: string | null;
  mailCategory: string | null;
  mailSubclass: string | null;
  month: number;
  totalPGross: number;
  totalPNet: number;
  totalGGross: number;
  totalGNet: number;
  totalEGross: number;
  totalENet: number;
  totalCGross: number;
  totalCNet: number;
  totalPgeGross: number;
  totalPgeNet: number;
  totalWeight5kg: number;
  totalWeight5kgActual: number;
  totalRitems: number;
  totalVitems: number;
  totalExpressItems: number;
  totalPGrossStr: string;
  totalPNetStr: string;
  totalGGrossStr: string;
  totalGNetStr: string;
  totalEGrossStr: string;
  totalENetStr: string;
  totalCGrossStr: string;
  totalCNetStr: string;
  totalPgeGrossStr: string;
  totalPgeNetStr: string;
  totalWeight5kgStr: string;
  totalWeight5kgActualStr: string;
  totalRitemsStr: string;
  totalVitemsStr: string;
  totalExpressItemsStr: string;
}

// Interface for report totals section
export interface CN56ReportTotals {
  sampledTotal: CN56TotalData;
  nonSampledTotal: CN56TotalData;
  allFlowTotal: CN56TotalData;
}

// Interface for each report section
export interface CN56ReportSection {
  headerGrouping: CN56HeaderGrouping;
  cn56ReportTotals: CN56ReportTotals;
  items: CN56DataItem[];
  caption?: string;
}

// Main data structure is an array of sections
export type CN56ReportData = CN56ReportSection[];

export interface CN56ReportResponse {
  // For HTML format, contains the table data
  data?: CN56ReportData;
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
}

export function useCN56Report() {
  return useMutation<CN56ReportResponse, Error, CN56ReportParams>({
    mutationFn: async (params: CN56ReportParams) => {
      // Create URLSearchParams to handle array parameters without brackets
      const searchParams = new URLSearchParams();
      
      // Add service types
      params.serviceTypes.forEach(type => {
        searchParams.append('serviceTypes', type);
      });
      
      // Add group by options
      params.groupBy.forEach((option: string) => {
        searchParams.append('groupBy', option);
      });
      
      // Add format type
      searchParams.append('formatType', params.formatType);
      
      // Add optional parameters
      if (params.fromQuarter) {
        searchParams.append('fromQuarter', params.fromQuarter.toString());
      }
      if (params.toQuarter) {
        searchParams.append('toQuarter', params.toQuarter.toString());
      }
      if (params.fromYear) {
        searchParams.append('fromYear', params.fromYear.toString());
      }
      if (params.toYear) {
        searchParams.append('toYear', params.toYear.toString());
      }
      if (params.table) {
        searchParams.append('table', params.table);
      }
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }

      const response = await axios.get(`/reports/cn56?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `CN56_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
} 